import InfoIcon from '@mui/icons-material/Info';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, IconButton, Theme } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import heatmapOverlay from '../../assets/images/Mask_Lmdpi.png';
import heatmapBackground from '../../assets/images/sensorInsoles.png';
import { HeatmapData } from '../../model/passive/heatmapData';
import { PassiveParameterType } from '../../model/passive/passiveParameterType';
import { Temperature } from '../../model/temperature';
import Heatmap from '../Heatmap/Heatmap';
import { HeatmapSizeEnum } from '../Heatmap/HeatmapSizeEnum';
import { defaultTransforms, HeatmapTransforms } from '../Heatmap/heatmapTransforms';
import IconLoader from '../IconLoader';

// Styled components approach instead of makeStyles
interface CardStyleProps {
  isAlertStyle?: boolean;
  showingInfo?: boolean;
}

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'isAlertStyle' && prop !== 'showingInfo',
})<CardStyleProps>(({ theme, isAlertStyle, showingInfo }) => ({
  height: '381px',
  width: '254px',
  display: 'flex',
  position: 'relative',
  flexDirection: 'column',
  border: '2px solid',
  borderColor: showingInfo
    ? theme.palette.primary.main
    : isAlertStyle
      ? '#D90B0A'
      : '#197F2B',
  borderRadius: '10px !important',
}));

const StyledCardMedia = styled(CardMedia)({
  display: 'flex !important',
  alignItems: 'center',
  marginTop: 15,
  flexDirection: 'column',
});

const StyledCardContent = styled(CardContent)({
  display: 'flex',
  flexDirection: 'row',
  padding: '0px 0px 0px 16px !important',
});

const InfoIconStyled = styled(InfoIcon, {
  shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded?: boolean }>(({ theme, expanded }) => ({
  height: 28,
  width: 28,
  color: expanded ? 'white' : theme.palette.primary.main,
}));

const AtRiskArea = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
});

const Circle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  height: '28px',
  width: '28px',
  borderRadius: '50px',
  backgroundColor: `${theme.palette.error.main} !important`,
  color: theme.palette.info.main,
}));

const PopOver = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'entering',
})<{ entering?: boolean }>(({ theme, entering }) => ({
  position: 'absolute',
  zIndex: 2,
  top: 0,
  right: 0,
  height: entering ? '381px' : 0,
  width: entering ? '254px' : 0,
  backgroundColor: theme.palette.primary.main,
  borderRadius: '6px',
  transform: 'rotate(0deg)',
  transition: 'all 250ms ease-in-out',
}));

const PopOverContent = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  margin: '40px 0px 0px 16px',
  color: 'white',
});

interface AlertStyleProps {
  isAlertStyle: boolean | undefined;
  showingInfo: boolean | undefined;
}

interface IAlertCardProps {
  onClick: (arg: string) => void;
  isAlert?: boolean;
  parameterType: PassiveParameterType;
  popoverContent: string;
  heatmapData: HeatmapData;
  unit?: Temperature;
}

const AlertCard: React.FC<IAlertCardProps> = ({
  onClick,
  isAlert,
  parameterType,
  popoverContent,
  heatmapData,
  unit,
}: IAlertCardProps) => {
  const { t } = useTranslation('cards');
  const [showInfo, setShowInfo] = useState<boolean>(false);


  const pressureTransforms: HeatmapTransforms = {
    scaleX: 0.44,
    scaleY: 0.49,
    translateX: -40,
    translateY: -60,
  };

  const temperatureTransforms: HeatmapTransforms = {
    scaleX: 0.41,
    scaleY: 0.49,
    translateX: -50,
    translateY: -40,
  };

  const selectTransforms = (type: PassiveParameterType) => {
    switch (type) {
      case PassiveParameterType.AlarmPressure:
        return pressureTransforms;
      case PassiveParameterType.AlarmTemperature:
        return temperatureTransforms;
      default:
        return defaultTransforms;
    }
  };

  const CardHeader = () => {
    return (
      <Box
        sx={{
          display: '-webkit-flex',
          flexDirection: 'row',
          WebkitJustifyContent: 'right',
        }}
      >
        <PopOver entering={showInfo}>
          <PopOverContent>
            <IconLoader name={parameterType} fill="white" />
            <Typography
              sx={{
                ml: 2,
              }}
              variant="h5"
              component="h2"
            >
              <strong>{t('alert.distribution', { type: parameterType })}</strong>
            </Typography>
          </PopOverContent>
          <Typography
            sx={{
              mt: 2,
              ml: 2,
              mr: 2,
              color: 'white',
            }}
            variant="body1"
          >
            {popoverContent}
          </Typography>
        </PopOver>

        <IconButton
          sx={{ ml: 1, display: 'inline-bloc', zIndex: 2 }}
          onClick={() => setShowInfo(!showInfo)}
        >
          <InfoIconStyled expanded={showInfo} />
        </IconButton>
      </Box>
    );
  };

  return (
    <StyledCard isAlertStyle={isAlert} showingInfo={showInfo}>
      {CardHeader()}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          cursor: 'pointer',
        }}
        onClick={() => onClick(parameterType)}
      >
        <StyledCardContent>
          <IconLoader name={parameterType} />
          <Typography
            sx={{
              ml: 2,
            }}
            variant="h5"
            component="h2"
          >
            <strong>{parameterType} Distribution</strong>
          </Typography>
        </StyledCardContent>

        <StyledCardMedia>
          <Heatmap
            height={130}
            width={130}
            magnitude={HeatmapSizeEnum.Small}
            data={heatmapData}
            type={parameterType}
            background={heatmapBackground}
            overlay={heatmapOverlay}
            transforms={selectTransforms(parameterType)}
            unit={unit}
          />
          {isAlert ? (
            <AtRiskArea>
              <Circle>
                <WarningAmberIcon
                  sx={{
                    height: 18,
                    color: 'white',
                    ml: '2px',
                  }}
                />
              </Circle>
              <Typography
                sx={{
                  mt: 1,
                }}
              >
                <strong>{t('alert.atRisk')}</strong>
              </Typography>
              <Typography>{parameterType}.</Typography>
            </AtRiskArea>
          ) : (
            <Typography
              sx={{
                mt: 7,
                wordWrap: 'break-word',
              }}
              align="center"
            >
              {parameterType === PassiveParameterType.AlarmPressure
                ? t('alert.noPressure')
                : t('alert.noTemperature')}
            </Typography>
          )}
        </StyledCardMedia>
      </Box>
    </StyledCard>
  );
};

export default AlertCard;
